// src/components/mdx/client/visualizations/CreativeFatigueFlowchart.client.tsx

"use client"

import { Fragment, type ReactNode } from "react"
import { motion } from "motion/react"
import {
  ArrowRight,
  ThumbsDown,
  Play,
  MousePointer,
  Repeat,
  DollarSign,
  Users,
  TrendingDown,
  Percent,
} from "lucide-react"

interface FlowchartCardProps {
  title: string
  items: { icon: ReactNode; text: string; trend: string }[]
  delay: number
}

const FlowchartCard = ({ title, items, delay }: FlowchartCardProps): JSX.Element => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay }}
    className="bg-gradient-to-br from-white to-blue-50 rounded-xl shadow-lg p-6 flex flex-col items-start space-y-4 w-full max-w-sm"
  >
    <h3 className="text-xl font-semibold text-blue-900">{title}</h3>
    <ul className="text-sm text-blue-800 space-y-3 w-full">
      {items.map((item, index) => (
        <li key={index} className="flex items-center space-x-3">
          <span className="text-blue-600">{item.icon}</span>
          <span className="flex-grow">{item.text}</span>
          <span className="text-purple-600 font-semibold">{item.trend}</span>
        </li>
      ))}
    </ul>
  </motion.div>
)

const Arrow = (): JSX.Element => (
  <motion.div
    initial={{ opacity: 0, scale: 0.5 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.5 }}
    className="hidden md:flex items-center justify-center w-16 h-16"
  >
    <ArrowRight className="text-blue-400" size={32} />
  </motion.div>
)

const ImprovedCustomFlowchart = (): JSX.Element => {
  const stages = [
    {
      title: "Early Warning Signs",
      items: [
        { icon: <ThumbsDown size={18} />, text: "Thumbstop Rate", trend: "↓" },
        { icon: <Play size={18} />, text: "Thruplay Rate", trend: "↓" },
        { icon: <MousePointer size={18} />, text: "CTR", trend: "↓" },
        { icon: <Repeat size={18} />, text: "Frequency", trend: "↑" },
      ],
    },
    {
      title: "Mid-Stage Indicators",
      items: [
        { icon: <DollarSign size={18} />, text: "CPM", trend: "↑" },
        { icon: <Users size={18} />, text: "First-Time Impression Ratio", trend: "↓" },
      ],
    },
    {
      title: "Late Stage Impact",
      items: [
        { icon: <Percent size={18} />, text: "Conversion Rate", trend: "↓" },
        { icon: <TrendingDown size={18} />, text: "ROAS", trend: "↓" },
      ],
    },
  ]

  return (
    <div className="my-12">
        <div className="flex flex-col md:flex-row items-center justify-center space-y-8 md:space-y-0 md:space-x-4">
        {stages.map((stage, index) => (
            <Fragment key={index}>
                {index > 0 && <Arrow />}
                <FlowchartCard title={stage.title} items={stage.items} delay={index * 0.2} />
            </Fragment>
        ))}
        </div>
    </div>
  )
}

export default function CreativeFatigueFlowchart(): JSX.Element {
  return <ImprovedCustomFlowchart />
}

